import React, { useState, useEffect, useRef } from 'react';
import QueueService from './QueueService';
import MarkdownParser from './MarkdownParser';
import { formatIST } from '../utils/dateUtils';
import './ChatWindow.css';
import { MdDone, MdDoneAll } from 'react-icons/md';

const ChatWindow = ({ chat, token, phone_number_id }) => {
  // 1) Define state and hooks at the top (unconditionally)
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // 2) You can read chat-related data safely
  const phone = chat?.user_id || null;
  const canReply = chat?.status || false;

  // 3) Auto-scroll to bottom after each render (when messages change)
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // 4) Fetch messages when phone changes (if phone is null, do nothing)
  useEffect(() => {
    if (!phone) {
      return; // phone is null => skip fetching
    }

    const fetchMessages = async () => {
      try {
        setLoading(true);
        const { messages: fetchedMessages } = await QueueService.FetchMessages(phone, phone_number_id);
        setMessages(fetchedMessages || []);
      } catch (err) {
        console.error('Error fetching messages:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [phone]);

  // 5) Handle sending messages (only if canReply)
  const handleSendMessage = async () => {
    if (!phone || !newMessage.trim() || !canReply) {
      return;
    }

    const tempId = Date.now();
    const newMsg = {
      id: tempId,
      message_text: newMessage,
      sent_by_system: true,
      timestamp: new Date().toISOString(),
      entity_type: 'customer',
      status: 'pending'
    };
    setMessages((prev) => [...prev, newMsg]);
    setNewMessage('');

    try {
      const response = await QueueService.SendWhatsappMessage(phone, newMessage, token, phone_number_id);
      if (response && response.status?.toLowerCase() === 'success') {
        // Optionally update the message with real status/timestamp from response
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === tempId
              ? { ...msg, status: 'sent', timestamp: new Date(new Date(response.timestamp || msg.timestamp).getTime() - (5 * 60 + 30) * 60 * 1000).toISOString() }
              : msg
          )
        );
      } else {
        // Remove the message or mark as failed
        setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
      }
    } catch (err) {
      setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
      // Optionally show an error to the user
    }
  };

  // 6) Send on Enter (but no shift)
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // 7) Format timestamps
  const formatTimestamp = (timeString) => {
    if (!timeString) return '';
    return formatIST(timeString, 'time');
  };

  const getMessageDate = (timeString) => {
    if (!timeString) return '';
    const messageDate = new Date(timeString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Reset hours to compare dates only
    messageDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    yesterday.setHours(0, 0, 0, 0);

    if (messageDate.getTime() === today.getTime()) {
      return 'Today';
    } else if (messageDate.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    } else {
      return messageDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  // Helper to get the latest status and status times
  const getStatusInfo = (msg) => {
    if (!msg.status_history || !Array.isArray(msg.status_history)) {
      return { status: msg.status, times: {} };
    }
    // Find the latest status for each type
    const statusOrder = ['sent', 'delivered', 'read'];
    const times = {};
    statusOrder.forEach((status) => {
      // Find the latest timestamp for this status
      const found = msg.status_history
        .filter((s) => s.status === status)
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
      if (found) {
        times[status] = found.timestamp;
      }
    });
    // Determine the highest status present
    let latestStatus = msg.status;
    for (let i = statusOrder.length - 1; i >= 0; i--) {
      if (times[statusOrder[i]]) {
        latestStatus = statusOrder[i];
        break;
      }
    }
    return { status: latestStatus, times };
  };

  // Helper to format time
  const formatStatusTime = (timeString) => {
    if (!timeString) return '';
    return formatIST(timeString, 'time');
  };

  // 8) Render message content via MarkdownParser
  const renderMessageContent = (msg) => (
    <MarkdownParser text={msg.message_text} />
  );

  // Render status icon and tooltip for system messages
  const renderStatusIcon = (msg) => {
    if (!msg.sent_by_system) return null;
    const { status, times } = getStatusInfo(msg);
    let icon = null;
    let color = '#888';
    if (status === 'sent') {
      icon = <MdDone size={18} color={color} />;
    } else if (status === 'delivered') {
      icon = <MdDoneAll size={18} color={color} />;
    } else if (status === 'read') {
      color = '#34b7f1'; // WhatsApp blue
      icon = <MdDoneAll size={18} color={color} />;
    }
    // Tooltip content
    const tooltip = (
      <div className="status-tooltip">
        <div>Sent: {times.sent ? formatStatusTime(times.sent) : '-'}</div>
        <div>Delivered: {times.delivered ? formatStatusTime(times.delivered) : '-'}</div>
        <div>Read: {times.read ? formatStatusTime(times.read) : '-'}</div>
      </div>
    );
    return (
      <span className="status-icon-wrapper">
        <span className="status-icon" tabIndex={0}>
          {icon}
          <span className="status-tooltip-container">{tooltip}</span>
        </span>
      </span>
    );
  };

  const renderMessages = () => {
    let currentDate = null;

    return messages.map((msg, index) => {
      const messageDate = getMessageDate(msg.timestamp);
      const showDateSeparator = messageDate !== currentDate;

      if (showDateSeparator) {
        currentDate = messageDate;
      }

      // Get status icon and info
      const { status, times } = getStatusInfo(msg);
      let icon = null;
      let color = '#888';
      if (status === 'sent') {
        icon = <MdDone size={18} color={color} />;
      } else if (status === 'delivered') {
        icon = <MdDoneAll size={18} color={color} />;
      } else if (status === 'read') {
        color = '#34b7f1'; // WhatsApp blue
        icon = <MdDoneAll size={18} color={color} />;
      }
      // Tooltip content
      const tooltip = (
        <div className="status-tooltip">
          <div>Sent: {times.sent ? formatStatusTime(times.sent) : '-'}</div>
          <div>Delivered: {times.delivered ? formatStatusTime(times.delivered) : '-'}</div>
          <div>Read: {times.read ? formatStatusTime(times.read) : '-'}</div>
        </div>
      );
      // Only show status for system messages
      const timeAndStatus = msg.sent_by_system ? (
        <span className="time-status-wrapper" tabIndex={0}>
          <span className="timestamp">{formatTimestamp(msg.timestamp)}</span>
          <span className="status-icon">{icon}</span>
          <span className="status-tooltip-container">{tooltip}</span>
        </span>
      ) : (
        <span className="timestamp">{formatTimestamp(msg.timestamp)}</span>
      );

      return (
        <React.Fragment key={msg.id || index}>
          {showDateSeparator && (
            <div className="message-date-separator">
              <span>{messageDate}</span>
            </div>
          )}
          <div className={`message ${msg.sent_by_system ? 'system' : 'customer'}`}
          >
            <div className="message-content">
              {renderMessageContent(msg)}
              {timeAndStatus}
            </div>
          </div>
        </React.Fragment>
      );
    });
  };

  // 9) Conditionally render UI:
  // If "chat" is not provided, show placeholder;
  // otherwise, show chat UI.
  if (!chat) {
    return (
      <div className="chat-window-container">
        <div className="chat-window-header">No Chat Selected</div>
        <div className="chat-window-messages">
          <div className="chat-window-message-placeholder">
            Please select a chat from the list.
          </div>
        </div>
      </div>
    );
  }

  // Otherwise, render the normal chat window
  return (
    <div className="chat-window-container">
      <div className="chat-window-header">
        {chat.name ? `${chat.name} (${phone})` : phone}
      </div>

      <div className="chat-messages">
        {loading && <div className="chat-window-loading">Loading messages...</div>}
        {renderMessages()}
        <div ref={messagesEndRef} />
      </div>

      {!canReply && (
        <div style={{ textAlign: 'center', color: 'red', margin: '0.5rem' }}>
          This chat is inactive. You cannot send messages.
        </div>
      )}

      <div className="chat-window-input-container">
        <input
          type="text"
          placeholder="Type a message"
          className="chat-window-input"
          value={newMessage}
          onKeyPress={handleKeyPress}
          onChange={(e) => setNewMessage(e.target.value)}
          disabled={!canReply}
          style={{
            backgroundColor: canReply ? 'white' : '#f0f0f0',
            color: canReply ? 'black' : 'gray',
          }}
        />
        <button
          className="chat-window-send-button"
          onClick={handleSendMessage}
          disabled={!canReply}
          style={{
            backgroundColor: canReply ? '#007bff' : '#ccc',
            cursor: canReply ? 'pointer' : 'not-allowed',
          }}
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default ChatWindow;
